import React, { useCallback, useEffect, useState } from 'react';
import { Table, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getFreeRoundsCampaignGamesData, getFreeRoundsCampaignGamesTotalData, getPromotionsLoadingStateData } from '../../../../../redux/selectors/backoffice/propmotions-selectors';
import { FilterValue, SorterResult, TablePaginationConfig } from 'antd/lib/table/interface';
import { getFreeRoundsCampaignGames } from 'redux/actions/backoffice/promotions-actions';

interface GamesTableProps {
    campaignId: string;
}

type SortOrder = 'DESCENDING' | 'ASCENDING';
interface SortConfig {
    sortKey: string;
    sortOrder: SortOrder;
}

const DEFAULT_SORT_CONFIG: SortConfig = { sortKey: 'id', sortOrder: 'DESCENDING' };
const DEFAULT_AMOUNT_OF_ITEMS_PER_PAGE = 20;
const DEFAULT_PAGINATION = { pageNumber: 1, pageSize: DEFAULT_AMOUNT_OF_ITEMS_PER_PAGE };

const GamesTable: React.FC<GamesTableProps> = ({ campaignId }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [searchTerm, setSearchTerm] = useState('');
    const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
    const [sortConfig, setSortConfig] = useState<SortConfig>(DEFAULT_SORT_CONFIG);
    const [tableHeight, setTableHeight] = useState(0);
    const games = useSelector(getFreeRoundsCampaignGamesData);
    const gamesTotal = useSelector(getFreeRoundsCampaignGamesTotalData);
    const isLoading = useSelector(getPromotionsLoadingStateData);

    const columns = [
        {
            title: t('Game ID'),
            dataIndex: 'id',
            key: 'id',
            width: 100,
            sorter: true,
        },
        {
            title: t('Game Name'),
            dataIndex: 'name',
            key: 'name',
            ellipsis: true,
            sorter: true,
        },
        {
            title: t('Bet Size'),
            dataIndex: 'betSize',
            key: 'betSize',
        },
        {
            title: t('Used'),
            dataIndex: 'used',
            key: 'used',
            render: (used: boolean) => used ? t('Yes') : t('No'),
        }
    ];

    const totalWidth: number = columns.reduce(
        (total: number, col: any) => total + (col.width || 0),
        0,
    );

    const calculateTableHeight = useCallback(() => {
        const height = window.innerHeight - 570;
        setTableHeight(height);
    }, []);

    useEffect(() => {
        calculateTableHeight();
        window.addEventListener('resize', calculateTableHeight);

        return () => window.removeEventListener('resize', calculateTableHeight);
    }, [calculateTableHeight]);

    const handlePagination = (page: number, pageSize?: number) => {
        setPaginationData({ pageNumber: page, pageSize: pageSize || DEFAULT_PAGINATION.pageSize });
    };

    const handleSort = (sorter: SorterResult<any> | SorterResult<any>[]) => {
        if (Array.isArray(sorter) && sorter.length && sorter[0].order) {
            setSortConfig({
                sortKey: sorter[0].field?.toString() || 'id',
                sortOrder: sorter[0].order === 'ascend' ? 'ASCENDING' : 'DESCENDING',
            });
        } else if (!Array.isArray(sorter) && sorter.field && sorter.order) {
            setSortConfig({
                sortKey: sorter.field.toString(),
                sortOrder: sorter.order === 'ascend' ? 'ASCENDING' : 'DESCENDING',
            });
        }
    };

    const onTableChange = (
        pagination: TablePaginationConfig,
        filters: Record<string, FilterValue | null>,
        sorter: SorterResult<any> | SorterResult<any>[]
    ) => {
        handlePagination(pagination.current ?? 1, pagination.pageSize);
        handleSort(sorter);
    };

    useEffect(() => {
        dispatch(getFreeRoundsCampaignGames({ campaignId, paginationData, sortConfig, gameNameSearchValue: searchTerm }));
    }, [campaignId, dispatch, paginationData, sortConfig, searchTerm]);

    return (
        <div className="pt-2 flex flex-col h-full">
            <div className="flex items-center mb-4">
                <Input.Search
                    placeholder={t('Search games')}
                    value={searchTerm}
                    onSearch={setSearchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="w-64"
                />
            </div>

            <Table
                loading={isLoading}
                virtual
                className="w-full"
                rowKey="id"
                size="small"
                columns={columns}
                dataSource={games}
                pagination={{
                    current: paginationData.pageNumber,
                    pageSize: paginationData.pageSize,
                    total: gamesTotal,
                    showSizeChanger: true,
                    onChange: handlePagination,
                }}
                scroll={{ y: tableHeight, x: totalWidth }}
                onChange={onTableChange}
            />
        </div>
    );
};

export default GamesTable; 